import { db } from '../db';
import { toast } from 'sonner';
import { t } from '@lingui/core/macro';
import { ERROR_TOAST_DURATION_MS } from '@/lib/toast';
import { ENV } from '../config/env';
import { getImpersonationEmail } from '../auth/impersonation';
import { APP_MODE_HEADER, WORKING_MODE_READ_ONLY_ERROR, getStoredAppMode } from '../app-mode';
import {
  extractMissingPermission,
  permissionDeniedDescription,
  permissionDeniedTitle,
} from './permission-copy';

const API_BASE_URL = ENV.VITE_API_BASE_URL;

/** Property path → what is wrong with it, as sent by the API (T-704). */
export type ApiFieldErrors = Record<string, string[]>;

// Custom API Error class with status code
export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public errorCode?: string,
    /**
     * Which inputs the server rejected, when it said. A form uses this to put
     * the message under the box that caused it instead of showing one toast
     * that names nothing.
     */
    public fields?: ApiFieldErrors
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * What the user is told when the API can't be reached — the server is down,
 * restarting, or behind a gateway that gave up on it. The browser's own words
 * for this ("Failed to fetch", "Load failed", "NetworkError when attempting to
 * fetch resource") describe the transport, not anything the user can act on.
 */
export const SERVER_UNAVAILABLE_MESSAGE = 'Server busy. Please try again later.';

/**
 * The same sentence, translated when it is raised. `SERVER_UNAVAILABLE_MESSAGE`
 * stays as the English source of truth; keep the two in step.
 */
function serverUnavailableMessage(): string {
  return t`Server busy. Please try again later.`;
}

/** `statusCode` of an `ApiError` raised because no response arrived at all. */
export const NETWORK_ERROR_STATUS = 0;

/** True when the request never reached the API, or a gateway answered for it. */
export function isServerUnavailableError(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    (error.statusCode === NETWORK_ERROR_STATUS || [502, 503, 504].includes(error.statusCode))
  );
}

/** Statuses a gateway sends when the API behind it is down or too slow. */
const GATEWAY_STATUSES = new Set([502, 503, 504]);

/**
 * `fetch`, with a rejection — which only ever means no response arrived —
 * turned into an `ApiError` carrying user-facing copy. An abort is the caller
 * cancelling (React Query does, on unmount), so it passes through untouched.
 */
async function send(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    console.warn('[yeschef] API unreachable', { url, error });
    throw new ApiError(serverUnavailableMessage(), NETWORK_ERROR_STATUS, 'NETWORK_ERROR');
  }
}

/** The fallback message for an error response whose body said nothing. */
function fallbackMessage(response: Response): string {
  return GATEWAY_STATUSES.has(response.status)
    ? serverUnavailableMessage()
    : t`API Error: ${response.statusText}`;
}

/**
 * The server's per-field messages, or an empty map when it did not send any.
 * Use in mutation `onError` to drive inline errors.
 */
export function getApiErrorFields(error: unknown): ApiFieldErrors {
  return error instanceof ApiError && error.fields ? error.fields : {};
}

/**
 * Pull `{ message, details: { fields } }` out of an error body. `message` can
 * arrive as an array from a validation pipe that predates the field map, in
 * which case the lines are joined rather than stringified into "a,b,c".
 *
 * `serverMessage` is left undefined when the body carried none, so the toast
 * copy below can use its own wording rather than "API Error: Bad Gateway".
 */
function parseErrorBody(
  body: unknown,
  fallback: string
): { message: string; serverMessage?: string; fields?: ApiFieldErrors } {
  const data = (body ?? {}) as {
    message?: unknown;
    details?: { fields?: unknown };
  };

  const serverMessage = Array.isArray(data.message)
    ? data.message.join('; ')
    : typeof data.message === 'string' && data.message
      ? data.message
      : undefined;

  const rawFields = data.details?.fields;
  const fields =
    rawFields && typeof rawFields === 'object' ? (rawFields as ApiFieldErrors) : undefined;

  return { message: serverMessage ?? fallback, serverMessage, fields };
}

/**
 * Extract the server-provided, user-facing message from a thrown request error,
 * falling back to `fallback` for non-API or unknown errors. Use this in mutation
 * `onError` handlers so the backend's specific message (e.g. a validation reason)
 * is shown instead of a generic "Failed to …".
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError && error.message) return error.message;
  return fallback;
}

/**
 * True when the error is a 409 Conflict raised by the API — e.g. creating a
 * record whose unique value already exists in the organization. Use this in
 * mutation `onError` handlers to show a Lingui-translated, entity-specific
 * conflict message instead of passing the backend's English string straight
 * through `getApiErrorMessage` (which the i18n tooling cannot localize).
 */
export function isConflictError(error: unknown): boolean {
  return error instanceof ApiError && error.statusCode === 409;
}

// Token management - imported dynamically to avoid circular dependencies
function getStoredAccessToken(): string | null {
  return localStorage.getItem('yeschef_access_token');
}

function isStoredTokenExpired(): boolean {
  const expiresAt = localStorage.getItem('yeschef_expires_at');
  if (!expiresAt) return true;
  return Date.now() > parseInt(expiresAt) - 60000;
}

interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
  queueOffline?: boolean;
  adminScope?: boolean;
  skipToast?: boolean;
}

class ApiClient {
  /**
   * Record the developer-facing detail of a permission denial. This is the only
   * place the raw permission string and raw server message are allowed to go —
   * T-105 keeps both out of the toast, where they meant nothing to a customer.
   */
  private logPermissionDenied(message?: string): void {
    const missingPermission = extractMissingPermission(message);
    const impersonatedEmail = sessionStorage.getItem('yeschef_impersonation_email');

    console.warn('[yeschef] permission denied', {
      missingPermission: missingPermission ?? '(not reported by API)',
      serverMessage: (message || '').trim() || '(empty)',
      ...(impersonatedEmail ? { impersonating: impersonatedEmail } : {}),
    });
  }

  private async getAuthHeaders(includeJsonContentType = true, adminScope = false): Promise<HeadersInit> {
    const headers: HeadersInit = {};
    let impersonatedEmail: string | null = null;

    if (includeJsonContentType) {
      headers['Content-Type'] = 'application/json';
    }

    const accessToken = getStoredAccessToken();
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;

      impersonatedEmail = getImpersonationEmail();
      if (impersonatedEmail) {
        headers['x-login-as-email'] = impersonatedEmail;
      }
    }

    // Tell the API which mode this request is made in: it refuses recipe writes
    // made in Working mode (YC-33). Read from storage, not React state, so it is
    // right for every caller, hooks or not.
    if (accessToken) {
      headers[APP_MODE_HEADER] = getStoredAppMode();
    }

    // Admin-scope is super-admin-only; only send it on authenticated non-impersonated requests.
    if (adminScope && accessToken && !impersonatedEmail) {
      headers['X-Admin-Scope'] = 'true';
    }

    return headers;
  }

  private async refreshTokenIfNeeded(): Promise<void> {
    if (!isStoredTokenExpired()) return;

    const refreshToken = localStorage.getItem('yeschef_refresh_token');
    if (!refreshToken) return;

    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.accessToken) {
          localStorage.setItem('yeschef_access_token', data.accessToken);
          if (data.idToken) {
            localStorage.setItem('yeschef_id_token', data.idToken);
          }
          if (data.expiresIn) {
            const expiresAt = Date.now() + data.expiresIn * 1000;
            localStorage.setItem('yeschef_expires_at', expiresAt.toString());
          }
        }
      }
    } catch (error) {
      console.error('Token refresh failed', error);
    }
  }

  private async queueRequest(
    endpoint: string,
    method: string,
    body?: unknown,
    headers?: HeadersInit
  ): Promise<void> {
    await db.offlineQueue.add({
      type: 'mutation',
      endpoint,
      method,
      body,
      headers: headers as Record<string, string>,
      timestamp: Date.now(),
      retries: 0,
      status: 'pending',
    });
  }

  private async isOnline(): Promise<boolean> {
    if (typeof navigator !== 'undefined' && 'onLine' in navigator) {
      return navigator.onLine;
    }
    return true;
  }

  private handleErrorToast(statusCode: number, message?: string, errorCode?: string): void {
    switch (statusCode) {
      case 403:
        // A Working-mode write is not a permission problem: say what it is
        // instead of sending the user to "Get help" (YC-33).
        if (errorCode === WORKING_MODE_READ_ONLY_ERROR) {
          toast.error(message || 'Switch to Build mode to edit recipes.', {
            duration: ERROR_TOAST_DURATION_MS,
          });
          break;
        }
        this.logPermissionDenied(message);
        toast.error(permissionDeniedTitle(message), {
          description: permissionDeniedDescription(),
          duration: ERROR_TOAST_DURATION_MS,
          action: {
            label: t`Get help`,
            onClick: () => {
              window.location.assign('/help-center');
            },
          },
        });
        break;
      case 404:
        toast.error(message || t`Resource not found`, { duration: ERROR_TOAST_DURATION_MS });
        break;
      case 409:
        toast.error(message || t`Conflict — this action could not be completed`, {
          duration: ERROR_TOAST_DURATION_MS,
        });
        break;
      case 500:
      case 502:
      case 503:
      case 504: {
        const errorMsg = message || (statusCode === 500 ? t`Server error` : t`Server busy`);
        toast.error(errorMsg, {
          description: t`Please try again later.`,
          duration: ERROR_TOAST_DURATION_MS,
        });
        // Dispatch a global event for debug modal
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('yeschef:global-error', {
              detail: errorMsg,
            })
          );
        }
        break;
      }
      // Don't show toast for 401 - handled by auth flow
      default:
        break;
    }
  }

  async request<T>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<T> {
    const {
      skipAuth = false,
      queueOffline = true,
      adminScope = false,
      skipToast = false,
      ...fetchOptions
    } = options;

    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
    const isFormDataBody =
      typeof FormData !== 'undefined' && fetchOptions.body instanceof FormData;
    
    // Refresh token if needed before making request
    if (!skipAuth) {
      await this.refreshTokenIfNeeded();
    }

    const headers = skipAuth
      ? (isFormDataBody ? {} : { 'Content-Type': 'application/json' })
      : await this.getAuthHeaders(!isFormDataBody, adminScope);

    const online = await this.isOnline();

    // If offline and it's a mutation, queue it
    if (!online && queueOffline && fetchOptions.method && fetchOptions.method !== 'GET') {
      const queueBody =
        typeof fetchOptions.body === 'string'
          ? JSON.parse(fetchOptions.body)
          : undefined;
      await this.queueRequest(
        endpoint,
        fetchOptions.method,
        queueBody,
        headers as HeadersInit
      );
      
      throw new Error('OFFLINE_QUEUED');
    }

    try {
      const response = await send(url, {
        // Every response is authenticated and tenant-scoped, so the browser HTTP
        // cache must never satisfy a request from disk. Freshness is React
        // Query's job; the transport always goes to the network.
        cache: 'no-store',
        ...fetchOptions,
        headers: {
          ...headers,
          ...fetchOptions.headers,
        },
      });

      if (!response.ok) {
        // Handle token refresh on 401
        if (response.status === 401 && !skipAuth) {
          await this.refreshTokenIfNeeded();
          const newHeaders = await this.getAuthHeaders(!isFormDataBody);
          const retryResponse = await send(url, {
            cache: 'no-store',
            ...fetchOptions,
            headers: {
              ...newHeaders,
              ...fetchOptions.headers,
            },
          });
          
          if (!retryResponse.ok) {
            const errorData = await retryResponse.json().catch(() => ({}));
            const parsed = parseErrorBody(errorData, fallbackMessage(retryResponse));
            const error = new ApiError(
              parsed.message,
              retryResponse.status,
              errorData.error,
              parsed.fields
            );
            if (!skipToast) this.handleErrorToast(retryResponse.status, parsed.serverMessage, errorData.error);
            throw error;
          }

          if (retryResponse.status === 204) return null as T;
          const retryText = await retryResponse.text();
          const trimmed = retryText.trim();
          return (trimmed ? JSON.parse(trimmed) : null) as T;
        }

        const errorData = await response.json().catch(() => ({}));
        const parsed = parseErrorBody(errorData, fallbackMessage(response));
        const error = new ApiError(
          parsed.message,
          response.status,
          errorData.error,
          parsed.fields
        );
        if (!skipToast) this.handleErrorToast(response.status, parsed.serverMessage, errorData.error);
        throw error;
      }

      if (response.status === 204) return null as T;
      const text = await response.text();
      const trimmed = text.trim();
      return (trimmed ? JSON.parse(trimmed) : null) as T;
    } catch (error) {
      if (error instanceof Error && error.message === 'OFFLINE_QUEUED') {
        throw error;
      }
      
      // If network error and mutation, queue it
      if (!online && queueOffline && fetchOptions.method && fetchOptions.method !== 'GET') {
        const queueBody =
          typeof fetchOptions.body === 'string'
            ? JSON.parse(fetchOptions.body)
            : undefined;
        await this.queueRequest(
          endpoint,
          fetchOptions.method,
          queueBody,
          headers as HeadersInit
        );
        throw new Error('OFFLINE_QUEUED');
      }
      
      throw error;
    }
  }

  async get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async put<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  async patch<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  async delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

export const api = new ApiClient();

// Background sync processor
export async function processOfflineQueue() {
  const online = await api['isOnline']();
  if (!online) return;

  const queue = await db.offlineQueue
    .where('status')
    .anyOf(['pending', 'processing'])
    .toArray();

  for (const item of queue) {
    try {
      await db.offlineQueue.update(item.id!, { status: 'processing' });

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        ...(item.headers || {}),
      };

      const accessToken = getStoredAccessToken();
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      const url = item.endpoint.startsWith('http')
        ? item.endpoint
        : `${API_BASE_URL}${item.endpoint}`;

      const response = await fetch(url, {
        method: item.method,
        headers,
        body: item.body ? JSON.stringify(item.body) : undefined,
      });

      if (response.ok) {
        await db.offlineQueue.update(item.id!, {
          status: 'completed',
        });
      } else {
        throw new Error(`Failed: ${response.statusText}`);
      }
    } catch {
      const retries = (item.retries || 0) + 1;
      if (retries >= 5) {
        await db.offlineQueue.update(item.id!, {
          status: 'failed',
          retries,
        });
      } else {
        await db.offlineQueue.update(item.id!, {
          status: 'pending',
          retries,
        });
      }
    }
  }
}

// Set up periodic sync
if (typeof window !== 'undefined') {
  // Process queue every 30 seconds when online
  setInterval(() => {
    processOfflineQueue().catch(console.error);
  }, 30000);

  // Process queue when coming back online
  window.addEventListener('online', () => {
    processOfflineQueue().catch(console.error);
  });
}
